import React, { useEffect, useMemo, useState } from 'react'
import { Box, CircularProgress, Divider, Grid, Stack } from '@mui/material'
import { DataGrid, GridColDef } from '@mui/x-data-grid'
import { useQuery } from '@tanstack/react-query'
import DataApi from 'api/faber/DataApi'
import ExternalApi from 'api/faber/ExternalApi'
import OneTimePackApi from 'api/faber/OneTimePackApi'
import ColorBox from 'common/Box/ColorWhiteBox'
import ProfileListInfo from 'common/FaberComponents/Layout/FaberColumnLayout'
import NormalText from 'common/Typography/NormalText'
import circlesOrbitColorPalette from 'common/ui/circlesColorTheme'
import { mapStatesToFrontendDropdown } from 'helpers/backendStatesMapping'
import OrbitSession from 'hooks/useCookies'
import { RefreshNetworkDialog } from 'pages/Customers/sharedComponents'
import ExternalActionMenuForOneUser from 'pages/Customers/sharedComponents/ExternalActionMenuForOneUser'
import SwitchWhitelistDialog from 'pages/Customers/sharedComponents/SwitchWhitelistDialog'
import { EsimRequestV2Dialog } from 'pages/Customers/sharedDialogs'
import DeletePaymentMethodDialog from 'pages/Customers/sharedDialogs/DeletePaymentMethodDialog'
import { TransformedFaberUserData } from 'types/api/faber/globalCustomers.type'
import { GetActiveUserPlansDetailsType, GetAllFaberUsersDetails, GetAllInactiveAndTerminatedOtpackUserPlansDetailsType } from 'types/api/faber/oneTimePack.type'

import CancelExternalPlanDialog from './CancelExternalPlanDialog'
import CustomerPlansCard from './CustomerPlansCard'
import DataRemainingCardV2 from './DataRemainingCardV2'
import EsimStatusCardV2 from './EsimStatusCardV2'
import JetpassVoucherTable from './JetpassVoucherTable'
import TerminatedCustomerPlans from './TerminatedCustomerPlans'

interface UserProfileProps {
  customerRow: TransformedFaberUserData
}

export default function ExternalUserProfile({ customerRow }: UserProfileProps) {
  const faberToken = OrbitSession.get('faberToken')

  const [iccid, setIccid] = useState('')

  // State management
  const [isRefreshNetworkDialogOpen, setIsRefreshNetworkDialogOpen] = useState(false)
  const [isEsimEmailDialogOpen, setIsEsimEmailDialogOpen] = useState(false)
  const [isSwitchWhitelistDialogOpen, setIsSwitchWhitelistDialogOpen] = useState(false)

  // delete user payment method dialog
  const [isDeletePaymentMethodDialogOpen, setIsDeletePaymentMethodDialogOpen] = useState(false)

  //DATA REAMINING CARD
  const [dataRemaining, setDataRemaining] = useState(0)
  const [otpackDataRemaining, setOtpackDataRemaining] = useState(0)

  // CX USER PLAN
  const [allInactiveUserPlans, setAllInactiveUserPlans] = useState<
    GetAllInactiveAndTerminatedOtpackUserPlansDetailsType[]
  >([])

  const [activeOtpackUserPlans, setActiveOtpackUserPlans] = useState<
    GetActiveUserPlansDetailsType[]
  >([])

  //ESIM
  const [esimStatus, setEsimStatus] = useState('')
  const [tooltipInfo, setTooltipInfo] = useState('')
  const [esimTextColor, setEsimTextColor] = useState('')
  const [whitelistId, setWhitelistId] = useState('')

  const [orderRows, setOrderRows] = useState<GridRowDataType[]>([])

  interface GridRowDataType {
    extOrderReference: string
    externalReference: string
    userId: string
    status: string
    tenant: string
    orderId: string
    catalogId: number
    catalogName: string
    completedAt?: Date
    notes?: string
  }

  const screenWidth = window.innerWidth
  const columns: GridColDef[] = [
    {
      field: 'extOrderReference',
      headerName: 'External Order Ref',
      width: screenWidth * 0.2,
      filterable: true
    },
    {
      field: 'orderId',
      headerName: 'Order ID',
      width: screenWidth * 0.2
    },
    {
      field: 'status',
      headerName: 'Status',
      width: screenWidth * 0.2
    },
    {
      field: 'catalogName',
      headerName: 'Catalog Name',
      width: screenWidth * 0.2
    },
    {
      field: 'cancelPlan',
      flex: 1,
      minWidth: 120,
      filterable: false,
      sortable: false,
      headerName: 'Cancel Plan',
      renderCell: (params: { row: GridRowDataType }) => {
        const { row } = params
        return (
          <CancelExternalPlanDialog
            orderID={row.orderId}
            userId={customerRow.userID}
          />
        )
      }
    }
  ]

  //GET ALL ACTIVE OTPACK USER PLANS API
  const {
    refetch: refetchActiveOtpackUserPlans,
    isFetching: isFetchingActiveOTPackUserPlans
  } = useQuery(
    ['getAllActiveOtpackUserPlans', customerRow.userID],
    async () => {
      return OneTimePackApi.getExternalAllActiveOtpackUserPlans(
        customerRow.userID,
        faberToken
      )
    },
    {
      onSuccess: (res) => {
        setActiveOtpackUserPlans(res.data.body.data)

        return res.data
      },
      onError: () => {
        return []
      },
      enabled: customerRow.otpack
    }
  )

  //GET ALL INACTIVE OTPACK USER PLANS API
  const {
    refetch: refetchAllInactiveAndTerminatedUserPlans,
    isFetching: isFetchingInactiveOTPackUserPlans
  } = useQuery(
    ['getAllInactiveAndTerminatedOtpackUserPlans', customerRow.userID],
    async () => {
      return OneTimePackApi.getExternalAllInactiveOtpackUserPlans(
        customerRow.userID,
        faberToken
      )
    },
    {
      onSuccess: (res) => {
        setAllInactiveUserPlans(res.data.body.data)
      },
      onError: () => {
        return []
      },
      enabled: customerRow.otpack
    }
  )

  //eSIM STATUS API (gets ICCID)
  const { isLoading: isEsimStatusLoading, refetch: refetchEsimStatus } =
    useQuery(
      ['get-esim-status', customerRow.userID],
      async () => {
        // External users need to call the external end point
        // instead of the internal/ops getEsimStatus()
        const { data } = await DataApi.getExternalEsimStatus(
          faberToken,
          customerRow.userID
        )
        return data
      },
      {
        onSuccess: (res) => {
          if (!res.data) {
            setEsimStatus('N/A')
            setIccid('')
            setTooltipInfo('No eSIM has been purchased.')
            setWhitelistId('')
          } else {
            setIccid(res.data?.iccid)
            setEsimStatus(res.data?.state)
            setWhitelistId(res.data?.whitelist)
          }
          switch (res.data?.state) {
            case 'RELEASED':
              setTooltipInfo(
                'The eSIM has been sent to the user but not downloaded into a device yet'
              )
              setEsimTextColor(circlesOrbitColorPalette.neutrals[500])

              break
            case 'DISABLED':
              setTooltipInfo(
                'The eSIM has been downloaded into a device but not turned on. Advise user to turn on the line from their phone settings'
              )
              setEsimTextColor('#ed6c02')
              break
            case 'ENABLED':
              setTooltipInfo(
                'The eSIM has been downloaded into a device and is active'
              )
              setEsimTextColor(circlesOrbitColorPalette.success.main)
              break
            case 'DELETED':
              setTooltipInfo(
                'The eSIM profile has been removed from the device'
              )
              setEsimTextColor(circlesOrbitColorPalette.error.main)
              break
            case 'ERROR':
              setTooltipInfo('Please perform an eSim switch the customer')
              setEsimTextColor(circlesOrbitColorPalette.error.main)
              break
          }
          return res.data
        },
        enabled: !!customerRow.userID
      }
    )

  //DATA REMAINING CARD API FOR SUBSCRIPTION USERS
  const { data: subdata } = useQuery(
    ['getOverviewCRDData', iccid],
    async () => {
      // Get external since this is for an external profile
      const getCDROverviewDataResponse = await DataApi.getExternalDataCDROverview(
        iccid,
        faberToken
      )
      return getCDROverviewDataResponse.data
    },
    {
      onSuccess: (res) => {
        return res
      },
      onError: () => {
        return {}
      },
      //TODO: CHECK IF NEED && SUBSCRIPTION
      enabled: !!iccid && customerRow.subscription
    }
  )

  const { data: orders } = useQuery(['getUserExternalOrders'], async () =>
    ExternalApi.getUserExternalOrders(customerRow.userID, faberToken)
  )

  useEffect(() => {
    refetchActiveOtpackUserPlans()
    refetchAllInactiveAndTerminatedUserPlans()
    refetchEsimStatus()
  }, [location, customerRow])

  useEffect(() => {
    if (subdata && customerRow.subscription) {
      setDataRemaining(subdata.totalDataAllocatedMB - subdata.totalDataUsedMB)
    } else {
      setDataRemaining(0)
    }
  }, [subdata])

  useEffect(() => {
    if (orders) {
      setOrderRows(orders?.data.data)
    }
  }, [orders])

  /* FUNCTION CALLS */
  const allFields = useMemo(() => {
    if (!customerRow.firstName && !customerRow.lastName) {
      return [
        {
          mainFieldTitle: 'Profile',
          fields: [
            {
              fieldName: 'Name',
              fieldValue: customerRow.fullName
            },
            {
              fieldName: 'Email',
              fieldValue: customerRow.userEmail
            }
          ]
        }
      ]
    }
    return [
      {
        mainFieldTitle: 'Profile',
        fields: [
          {
            fieldName: 'First Name',
            fieldValue: customerRow.firstName
          },
          {
            fieldName: 'Last Name',
            fieldValue: customerRow.lastName
          },
          {
            fieldName: 'Email',
            fieldValue: customerRow.userEmail
          }
        ]
      }
    ]
  }, [customerRow])

  // EVENT HANDLER FUNCTIONS

  const handleRefreshNetworkDialogOpen = () => {
    setIsRefreshNetworkDialogOpen(true)
  }

  const handleRefreshNetworkDialogClose = () => {
    setIsRefreshNetworkDialogOpen(false)
  }

  const handleEsimDialogOpen = () => {
    setIsEsimEmailDialogOpen(true)
  }

  const handleEsimDialogClose = () => {
    setIsEsimEmailDialogOpen(false)
  }

  const handleSwitchWhitelistDialogOpen = () => {
    setIsSwitchWhitelistDialogOpen(true)
  }
  const handleSwitchWhitelistDialogClose = () => {
    setIsSwitchWhitelistDialogOpen(false)
  }

  const updateUserDetails = () => {
    refetchEsimStatus()
  }

  const handleDeletePaymentMethodDialogOpen = () => {
    setIsDeletePaymentMethodDialogOpen(true)
  }

  const handleDeletePaymentMethodDialogClose = () => {
    setIsDeletePaymentMethodDialogOpen(false)
  }

  const transformedRowInformation = useMemo(() => {
    const newCustomerRow: TransformedFaberAndGlobalUsersType = {
      ...customerRow,
      iccid: iccid
    }
    return newCustomerRow
  }, [customerRow, iccid])

  return (
    <>
      {/* ALL DIALOGS FOR ACTIONS HERE */}

      {/* When Action -> Refresh */}
      <RefreshNetworkDialog
        faberToken={faberToken}
        handleRefreshNetworkDialogClose={handleRefreshNetworkDialogClose}
        refreshNetworkDialogOpen={isRefreshNetworkDialogOpen}
        customerRow={transformedRowInformation}
        isExternal={true} // External profile needs to call the external api
      />

      {/* When Action -> Switch Whitelist */}
      <SwitchWhitelistDialog
        userId={customerRow.userID}
        iccid={iccid}
        esimWhitelist={whitelistId}
        isDialogOpen={isSwitchWhitelistDialogOpen}
        handleDialogClose={handleSwitchWhitelistDialogClose}
        isExternal={true}
      />

      {/* When Action -> eSIM replacement / Resend eSIM activation email */}
      <EsimRequestV2Dialog
        faberToken={faberToken}
        handleRequestEsimDialogClose={handleEsimDialogClose}
        requestEsimDialog={isEsimEmailDialogOpen}
        customerRow={transformedRowInformation}
        productType='otp'
        updateUserDetails={updateUserDetails}
        isExternal={true}
      />

      <DeletePaymentMethodDialog
        deletePaymentMethodDialogOpen={isDeletePaymentMethodDialogOpen}
        handleDeletePaymentMethodDialogClose={handleDeletePaymentMethodDialogClose}
        userID={customerRow.userID}
        isExternal={true}
      />

      <Stack spacing={3} mt={3}>
        <Stack>
          <NormalText isBold variant='h3'>
            Check the following steps to troubleshoot network issues:
          </NormalText>
          <NormalText>
            {' '}
            <em>
              Please get customers to take screenshots at each stage and send it
              as the screenshots will be needed if issue needs to be escalated
            </em>
          </NormalText>
        </Stack>
        <Grid container spacing={4}>
          <Grid item>
            <Box
              sx={{
                backgroundColor: '#FFFFFF',
                borderRadius: '34px',
                p: '24px',
                maxWidth: '215px',
                maxHeight: '280px',
                position: 'relative'
              }}
            >
              {(isFetchingActiveOTPackUserPlans ||
                isFetchingInactiveOTPackUserPlans) &&
                customerRow ? (
                <CircularProgress />
              ) : (
                <DataRemainingCardV2
                  dataRemaining={dataRemaining}
                  customerRow={customerRow}
                  otpackDataRemaining={otpackDataRemaining}
                />
              )}
            </Box>
          </Grid>

          <Grid item>
            {isEsimStatusLoading ? (
              <CircularProgress />
            ) : (
              <EsimStatusCardV2
                esimStatus={esimStatus}
                tooltipInfo={tooltipInfo}
                esimTextColor={esimTextColor}
                whitelist={whitelistId}
              />
            )}
          </Grid>
        </Grid>

        <Grid item>
          <ColorBox>

            <Stack
              flexDirection={'row'}
              sx={{ justifyContent: 'flex-end', mb: 2 }}
            >
              {/* This is different from the ActionMenuForOneUser, because for external */}
              {/* users, they need a different set of features, and we don't want to  */}
              {/* expose all the features to them */}
              <ExternalActionMenuForOneUser
                productType='otp'
                handleEsimRequestDialogOpen={() => void 0}
                handleRefreshNetworkDialogOpen={handleRefreshNetworkDialogOpen}
                handleWinbackDialogOpen={() => void 0}
                handleSuspendOrUnsuspendDialogOpen={() => void 0}
                showRefreshNetworkButton
                handleEsimRequestDialogV2Open={handleEsimDialogOpen}
                handleCancelSubDialogOpen={() => void 0}
                handleUserPlanDialogOpen={() => void 0}
                handleUserEditDetailsDialogOpen={() => void 0}
                userId={customerRow.userID}
                handleSwitchWhitelistDialogOpen={handleSwitchWhitelistDialogOpen}
                handleDeletePaymentMethodDialogOpen={handleDeletePaymentMethodDialogOpen}
              />
            </Stack>

            <Box sx={{ p: 2 }}>
              {allFields.map((field, index) => (
                <React.Fragment key={index + field.mainFieldTitle}>
                  {field.fields && field.fields.length > 0 && (
                    <ProfileListInfo
                      title={field.mainFieldTitle}
                      fields={field.fields.map((item) => ({
                        fieldName: item.fieldName,
                        fieldValue: item.fieldValue || ''
                      }))}
                    >
                      {/* TO DO: WHEN EDIT USER IS UP FOR ONE TIME PACK */}
                      {/* <EditIconImg onEdit={handleUserDialogOpen} /> */}
                    </ProfileListInfo>
                  )}
                  {index === 0 && (
                    <>
                      <ProfileListInfo
                        proportion={4}
                        title=''
                        fields={[
                          {
                            fieldName: 'MSISDN',
                            fieldValue: customerRow.msisdn
                          },
                          {
                            fieldName: 'ICCID',
                            fieldValue: iccid
                          },

                          {
                            fieldName: 'Customer Id',
                            fieldValue: customerRow.userID
                          }
                        ]}
                      />
                    </>
                  )}
                  {index === 1 && (
                    <ProfileListInfo
                      proportion={4}
                      title=''
                      fields={[
                        {
                          fieldName: 'Account Status',
                          fieldValue: mapStatesToFrontendDropdown(
                            customerRow.planStatus ? customerRow.planStatus : ''
                          )
                        }
                      ]}
                    />
                  )}
                  {index !== allFields.length - 1 && (
                    <Divider sx={{ width: '100%' }} />
                  )}
                </React.Fragment>
              ))}
            </Box>
          </ColorBox>
        </Grid>

        <ColorBox>
          <CustomerPlansCard
            setOtpackDataRemaining={setOtpackDataRemaining}
            activeOtpackUserPlans={activeOtpackUserPlans}
            allInactiveUserPlans={allInactiveUserPlans}
            isExternal={true}
            userId={customerRow.userID}
          />
        </ColorBox>

        <ColorBox>
          <TerminatedCustomerPlans
            isExternal={true}
          />
        </ColorBox>

        <ColorBox>
          <NormalText isBold>Orders</NormalText>
          <Grid item xs={12} height={640}>
            <DataGrid
              columns={columns}
              rows={orderRows}
              getRowId={(row) => row.orderId}
              onRowClick={(row) => console.log(row)}
              rowsPerPageOptions={[10]}
              pageSize={10}
            />
          </Grid>
        </ColorBox>

        <ColorBox>
          <>
            <NormalText isBold variant='h5'>
              Jetpass Vouchers
            </NormalText>
            <JetpassVoucherTable userId={customerRow.userID} isExternal={true} />
          </>
        </ColorBox>

      </Stack>
    </>
  )
}

export interface TransformedFaberUsersType extends GetAllFaberUsersDetails {
  iccid: string
}

export interface TransformedFaberAndGlobalUsersType
  extends TransformedFaberUserData {
  iccid: string
}
